import type { Viewport, BoundingBox } from "../state/types";

const MIN_SCALE = 0.25;
const MAX_SCALE = 4.0;

export function clampScale(scale: number): number {
  return Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
}

export function zoomAtPoint(
  viewport: Viewport,
  canvasX: number,
  canvasY: number,
  delta: number,
): Viewport {
  const factor = delta > 0 ? 0.9 : 1.1;
  const newScale = clampScale(viewport.scale * factor);
  const ratio = newScale / viewport.scale;

  return {
    ...viewport,
    scale: newScale,
    x: canvasX - (canvasX - viewport.x) * ratio,
    y: canvasY - (canvasY - viewport.y) * ratio,
  };
}

export function fitToWidth(
  viewport: Viewport,
  pageWidth: number,
  canvasWidth: number,
): Viewport {
  const scale = clampScale(canvasWidth / pageWidth);
  return {
    ...viewport,
    scale,
    x: 0,
    y: 0,
  };
}

/**
 * R-164 — Fit the entire page within the viewport. Chooses the smaller of
 * the horizontal and vertical ratios so neither dimension overflows, then
 * centers the bitmap in the canvas (so a portrait image in a landscape
 * canvas has equal slack on left/right, and vice versa).
 */
export function fitToPage(
  viewport: Viewport,
  pageWidth: number,
  pageHeight: number,
  canvasWidth: number,
  canvasHeight: number,
): Viewport {
  const scale = clampScale(
    Math.min(canvasWidth / pageWidth, canvasHeight / pageHeight),
  );
  const x = (canvasWidth - pageWidth * scale) / 2;
  const y = (canvasHeight - pageHeight * scale) / 2;
  return {
    ...viewport,
    scale,
    x,
    y,
  };
}

/**
 * R-188 — pan the viewport so a normalized zone's center pixel lands at the
 * center of the canvas. Scale + rotation are preserved (this only translates).
 *
 * `zone` is normalized (0..1) relative to the page bitmap (matches both the
 * OCR `boundingBox` and the admin-drawn `zoneDefinition` shapes). `page` is the
 * bitmap's pixel size; `canvas` is the rendered canvas CSS size.
 */
export function centerOnZone(
  viewport: Viewport,
  zone: Pick<BoundingBox, "x" | "y" | "width" | "height">,
  page: { width: number; height: number },
  canvas: { width: number; height: number },
): Viewport {
  const centerPxX = (zone.x + zone.width / 2) * page.width;
  const centerPxY = (zone.y + zone.height / 2) * page.height;
  return {
    ...viewport,
    x: -(centerPxX * viewport.scale - canvas.width / 2),
    y: -(centerPxY * viewport.scale - canvas.height / 2),
  };
}

/**
 * R-188 — is the zone's center already comfortably inside the viewport? Used
 * to skip the auto-center pan when the focused field is on-screen, so the
 * operator's manual positioning isn't disturbed needlessly. A 10% margin keeps
 * a zone hugging the very edge from counting as "visible."
 */
export function isZoneVisible(
  viewport: Viewport,
  zone: Pick<BoundingBox, "x" | "y" | "width" | "height">,
  page: { width: number; height: number },
  canvas: { width: number; height: number },
  margin = 0.1,
): boolean {
  const centerPxX = (zone.x + zone.width / 2) * page.width;
  const centerPxY = (zone.y + zone.height / 2) * page.height;
  const visibleLeft = -viewport.x / viewport.scale;
  const visibleTop = -viewport.y / viewport.scale;
  const visibleWidth = canvas.width / viewport.scale;
  const visibleHeight = canvas.height / viewport.scale;
  return (
    centerPxX > visibleLeft + visibleWidth * margin &&
    centerPxX < visibleLeft + visibleWidth * (1 - margin) &&
    centerPxY > visibleTop + visibleHeight * margin &&
    centerPxY < visibleTop + visibleHeight * (1 - margin)
  );
}

export function rotate90(viewport: Viewport): Viewport {
  return {
    ...viewport,
    rotation: (viewport.rotation + 90) % 360,
  };
}

/**
 * F-110 — `devicePixelRatio` is the BASE transform, not an afterthought.
 *
 * The backing store is sized `paneCSS × dpr`, while everything the viewport
 * describes — x, y, scale, bbox coords, pointer math — is in CSS pixels.
 * Re-basing to identity here (as this did) painted CSS-pixel geometry into a
 * device-pixel buffer: on a 2× display the page came out at roughly half size,
 * and the fit calculation disagreed with the pixels. Basing at dpr keeps every
 * caller in CSS pixels and confines the ratio to this one line.
 */
export function applyTransform(
  ctx: CanvasRenderingContext2D,
  viewport: Viewport,
  devicePixelRatio = 1,
): void {
  ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
  ctx.translate(viewport.x, viewport.y);
  ctx.scale(viewport.scale, viewport.scale);

  if (viewport.rotation !== 0) {
    // Rotate around center of canvas
    const rad = (viewport.rotation * Math.PI) / 180;
    ctx.rotate(rad);
  }
}
